'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { company, addressLines } from '@/lib/company';

// The form drags react-hook-form + zod (a ~300 KB chunk) — lazy-load it AND
// gate it on approach, so the chunk neither weighs down the homepage entry
// bundle nor executes inside the load window (task-28 mobile LCP budget).
// The placeholder reserves the form's height so the swap causes no layout
// shift.
const ContactForm = dynamic(() => import('./ContactForm').then((m) => m.ContactForm), {
  ssr: false,
  loading: () => <div style={{ minHeight: '480px' }} aria-hidden="true" />,
});

export function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || typeof IntersectionObserver === 'undefined') {
      // No IO support — just load the form on the next frame.
      const raf = requestAnimationFrame(() => setNear(true));
      return () => cancelAnimationFrame(raf);
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: '150% 0%' },
    );
    io.observe(section);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-16 md:py-32"
      style={{
        backgroundColor: 'rgba(12,12,12,0.65)',
        width: '100%',
        boxSizing: 'border-box',
        paddingLeft: '1.5rem',
        paddingRight: '1.5rem',
      }}
    >
      <div
        style={{
          maxWidth: '72rem',
          marginLeft: 'auto',
          marginRight: 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >

        {/* Top Divider */}
        <div
          style={{
            height: '1px',
            width: '6rem',
            backgroundColor: 'rgba(255,255,255,0.2)',
            marginBottom: '3rem',
          }}
        />

        {/* Title & Subtitle - Centered */}
        <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
          <span className="eyebrow" style={{ marginBottom: '1.5rem' }}>Start a project</span>
          <h2
            className="font-six-caps"
            style={{
              fontSize: 'clamp(80px, 10vw, 140px)',
              lineHeight: 0.85,
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '1.5rem',
            }}
          >
            LET&apos;S TALK
          </h2>
          <p
            style={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: 'clamp(1rem, 1.5vw, 1.125rem)',
              fontWeight: 300,
              maxWidth: '36rem',
              marginLeft: 'auto',
              marginRight: 'auto',
              lineHeight: 1.6,
            }}
          >
            Tell us what you want to build, or ask about one of our products.
            Your message goes directly to the team.
          </p>
        </div>

        {/* Contact Form */}
        <div
          style={{
            width: '100%',
            maxWidth: '42rem',
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: '3rem',
          }}
        >
          {near ? <ContactForm /> : <div style={{ minHeight: '480px' }} aria-hidden="true" />}
        </div>

        {/* Subtle Divider */}
        <div
          style={{
            height: '1px',
            width: '100%',
            maxWidth: '32rem',
            backgroundColor: 'rgba(255,255,255,0.1)',
            marginBottom: '2.5rem',
          }}
        />

        {/* Contact Info Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem',
            width: '100%',
            maxWidth: '48rem',
            textAlign: 'center',
          }}
        >
          {/* Email */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
            <p
              style={{
                fontSize: '10px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.3em',
                color: 'rgba(255,255,255,0.55)',
              }}
            >
              Email
            </p>
            <a
              href={`mailto:${company.email}`}
              style={{
                fontSize: '1rem',
                fontWeight: 300,
                color: 'rgba(255,255,255,0.7)',
                textDecoration: 'none',
                wordBreak: 'break-all',
              }}
            >
              {company.email}
            </a>
          </div>

          {/* Location */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
            <p
              style={{
                fontSize: '10px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.3em',
                color: 'rgba(255,255,255,0.55)',
              }}
            >
              Registered office
            </p>
            <address
              style={{
                fontSize: '1rem',
                fontWeight: 300,
                color: 'rgba(255,255,255,0.7)',
                fontStyle: 'normal',
              }}
            >
              {addressLines[0]}
              <br />
              {addressLines[1]}
            </address>
          </div>

          {/* Phone */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
            <p
              style={{
                fontSize: '10px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.3em',
                color: 'rgba(255,255,255,0.55)',
              }}
            >
              Phone
            </p>
            <a
              href={`tel:${company.telHref}`}
              style={{
                fontSize: '1rem',
                fontWeight: 300,
                color: 'rgba(255,255,255,0.7)',
                textDecoration: 'none',
              }}
            >
              {company.phone}
            </a>
          </div>
        </div>

      </div>
    </section>
  );
}
